import {
  type FilterBuilderProps,
  type RowClickActionProps,
  type SearchAndFiltersProps,
  type StyleConfigProps,
  type TableGroupByProps,
  type UnifiedBulkActionsProps,
  type UnifiedOtherActionsProps,
  type UnifiedRowActionsProps,
  type VisibilityElementProps,
  type WrappedElementProps,
} from '@blaze-runtime/engine-domain';
import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';
import {
  type ActionsDisplayMode,
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type CanvasElementWithDirtyFieldProps,
  type CanvasElementWithFeaturesConfigProps,
  type ComponentFeatureConditionsModel,
  type ElementLabelProps,
  type ElementTitleStyleProps,
  type ExpressionObj,
  type FieldSortConfig,
  type GenericSize,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputFontStyleOptions,
  type PropertiesConfigInputSpacingAndAlignmentModel,
  type PropertiesConfigInputTableColumnModel,
  type PropertiesConfigInputTableConditionalFormatModel,
  type PropertiesConfigInputVisibilityForGroupsModel,
  type PropertiesConfigInputVisualEffectModel,
  type ResizeBehavior,
  type UnifiedActionKind,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

type PropsModel = CanvasElementPropertiesWithData &
  CanvasElementWithDirtyFieldProps &
  CanvasElementWithFeaturesConfigProps &
  RowClickActionProps &
  SearchAndFiltersProps &
  FilterBuilderProps &
  StyleConfigProps &
  TableGroupByProps &
  UnifiedBulkActionsProps &
  UnifiedOtherActionsProps &
  UnifiedRowActionsProps &
  VisibilityElementProps &
  WrappedElementProps;

export type TablePropsModel = PropsModel & {
  content: {
    actions: {
      disableActionWhenPending: boolean;
    };
    title: ElementLabelProps;
    table: {
      actionsDisplayMode: ActionsDisplayMode;
      actionsView: UnifiedActionKind;
      alwaysVisibleActions: boolean;
      alwaysVisibleColumnsCount: number;
      alwaysVisibleFirst: boolean;
      autoSelectFirstRow: boolean;
      bulkActionsDisplayMode: ActionsDisplayMode;
      disableUserSort: boolean;
      editable: boolean;
      editableConditions?: ComponentFeatureConditionsModel;
      editableExpression?: ExpressionObj[];
      emptyTableLabel: string;
      footerAlwaysVisible: boolean;
      headerAlwaysVisible: boolean;
      origin: 'default' | 'wizard';
      showColumnHeaders: boolean;
      singleSelect: boolean;
      usePagination: boolean; // todo remove when `behavior` implemented
    };
    sort: FieldSortConfig | FieldSortConfig[];
    pagination: {
      enabled: boolean;
      server: boolean;
      count: number;
    };
    columns: PropertiesConfigInputTableColumnModel[];
    columnsVisibility?: { visible: PropertiesConfigInputVisibilityForGroupsModel };
    previewData: any;
    conditionalFormatting: {
      enabled: boolean;
      formats: PropertiesConfigInputTableConditionalFormatModel[];
    };
  };
  style: {
    fontSize: ValueWithUnitOfMeasure;
    fontFamily: string;
    lineHeight: ValueWithUnitOfMeasure;
    general: {
      title: PropertiesConfigInputColorModel;
      accent: PropertiesConfigInputColorModel;
      visualEffect: PropertiesConfigInputVisualEffectModel;
    };
    titleBlock: ElementTitleStyleProps;
    header: {
      background: PropertiesConfigInputColorModel;
      label: PropertiesConfigInputColorModel;
    };
    container: {
      outline: PropertiesConfigInputColorModel;
      outlineWidth: ValueWithUnitOfMeasure;
    };
    table: {
      text: PropertiesConfigInputColorModel;
      background: PropertiesConfigInputColorModel;
      selectedRow: PropertiesConfigInputColorModel;
      hover: PropertiesConfigInputColorModel;
      lines: PropertiesConfigInputColorModel;
      hideColumnsBorder: boolean;
      rowHeight: GenericSize; // Known as Row Padding
      rowHeightBehavior: ResizeBehavior;
      rowLineClamp: number;
      rowNumbering?: boolean;
      radius: BorderRadiusValue;
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
      group: {
        text: PropertiesConfigInputColorModel;
        style: PropertiesConfigInputFontStyleOptions;
        fontSize: ValueWithUnitOfMeasure;
        fontFamily: string;
        fontWeight: FontWeightOption;
        colorGenOptions: 'generate' | 'preselected';
        bgBaseColor: PropertiesConfigInputColorModel;
        bgLevel1: PropertiesConfigInputColorModel;
        bgLevel2: PropertiesConfigInputColorModel;
        bgLevel3: PropertiesConfigInputColorModel;
      };
    };
    submenu: {
      value: PropertiesConfigInputColorModel;
      background: PropertiesConfigInputColorModel;
      outline: PropertiesConfigInputColorModel;
      radius: BorderRadiusValue;
    };
    booleanCol: {
      checkedBackground: PropertiesConfigInputColorModel;
      checkedMark: PropertiesConfigInputColorModel;
      uncheckedBackground: PropertiesConfigInputColorModel;
      uncheckedMark: PropertiesConfigInputColorModel;
    };
  };
};

export class TableCellExpectedDownError extends Error {}
